import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to fetch prediction data from Supabase.
 *
 * The predictions table stores one row per prediction type (btts, outcome, etc.)
 * with the prediction array in a JSONB `data` column. RLS ensures only
 * authenticated users can read.
 */
function usePredictions() {
  const [predictions, setPredictions] = useState({
    btts: [], outcome: [], draw: [], correct_score: [], over25: [], htft: [], ultimate: [], ai_picks: [],
  });
  const [aiPicksSummary, setAiPicksSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPredictions() {
      setLoading(true);
      setError(null);

      try {
        const { data: rows, error: dbError } = await supabase
          .from('predictions')
          .select('type, data, metadata, generated_at');

        if (dbError) {
          setError(dbError.message);
          setLoading(false);
          return;
        }

        if (!rows || rows.length === 0) {
          // No data yet — keep defaults
          setLoading(false);
          return;
        }

        const combined = {
          btts: [], outcome: [], draw: [], correct_score: [],
          over25: [], htft: [], ultimate: [], ai_picks: [],
          acca: [],
          generated_at: null,
        };

        let summary = null;

        for (const row of rows) {
          if (row.type in combined) {
            combined[row.type] = row.data || [];
          }
          // Use the first non-null generated_at
          if (row.generated_at && !combined.generated_at) {
            combined.generated_at = row.generated_at;
          }
          // Extract ai_picks metadata
          if (row.type === 'ai_picks' && row.metadata) {
            summary = row.metadata.analysis_summary || null;
          }
        }

        if (!cancelled) {
          setPredictions(combined);
          setAiPicksSummary(summary);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPredictions();

    return () => { cancelled = true; };
  }, []);

  return { predictions, aiPicksSummary, loading, error };
}

export default usePredictions;
